/** Knox Service API client */

import i18next from 'i18next';
import { toast } from 'sonner';

const KNOX_API_BASE = import.meta.env.VITE_KNOX_API_URL ?? '';

export interface AuthResponse {
  user_id: string;
  org_id?: string;
  name: string;
  email: string;
  avatar?: string;
  matrix_user_id?: string;
  matrix_access_token?: string;
  matrix_homeserver_url?: string;
  default_workspace_id?: string;
  language?: string;
}

export interface LoginParams {
  email: string;
  password: string;
}

export interface RegisterParams {
  email: string;
  password: string;
  name: string;
  invitation_token?: string;
  org_name?: string;
}

export interface InviteInfoResponse {
  id: string;
  applicant_email: string;
  license_days: number;
  member_limit: number;
  expires_at: string;
  is_valid: boolean;
}

export interface CheckEmailResponse {
  exists: boolean;
  has_password: boolean;
  auth_providers?: string[];
}

export interface CreateInviteResponse {
  id: string;
  token: string;
  join_code?: string;
  invite_url: string;
  role: string;
  expires_at: string;
  invited_at: string;
}

export interface AcceptInviteResponse {
  org_id: string;
  org_name: string;
  default_workspace_id?: string;
}

export interface Room {
  workspace_id: string;
  matrix_room_id: string;
  room_name: string;
  room_type: 'inbox' | 'room';
  description?: string;
  created_by?: string;
  readiness_state?: 'initializing' | 'binding_plugin' | 'waiting_bots' | 'ready' | 'degraded' | 'failed' | 'unsupported';
  readiness_reason?: string;
  can_retry?: boolean;
  retry_action?: string;
  last_error?: string;
}

export interface CreateRoomParams {
  name: string;
  description?: string;
}

export type InboxCategory = 'alert' | 'invitation' | 'notification' | 'onboarding' | 'onboarding_side_quest';
export type InboxPriority = 'critical' | 'high' | 'medium' | 'low';
export type InboxStatus = 'open' | 'resolved' | 'dismissed';

export interface InboxItemAction {
  label?: string;
  action: string;
  payload?: string;
}

export interface InboxItem {
  id: string;
  workspace_id: string;
  category: InboxCategory;
  title: string;
  summary?: string;
  status: InboxStatus;
  priority: InboxPriority;
  actions?: string; // JSON string of InboxItemAction[]
  source_type?: string;
  source_ref?: string;
  related_room_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ListRoomsResponse {
  items: Room[];
  total_count: number;
}


export interface UserProfile {
  id?: string;
  email?: string;
  name?: string;
  avatar?: string;
  org_id?: string;
  org_name?: string;
  org_slug?: string;
  role?: string;
  theme?: string;
  language?: string;
}

export interface UpdateProfileParams {
  name?: string;
  avatar?: string;
  theme?: string;
  language?: string;
}

export interface UploadProfileAvatarResponse {
  avatar?: string;
  url?: string;
}

export interface OrgInfo {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  role: string;
  status: string;
}

export interface MemberInfo {
  user_id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  status: string;
  joined_at: string;
}

export interface MembersResponse {
  members?: MemberInfo[];
  total?: number;
  active_count?: number;
  disabled_count?: number;
  page?: number;
  page_size?: number;
}

export interface OrgInviteInfo {
  id: string;
  org_id: string;
  org_name: string;
  org_logo?: string;
  inviter_name?: string;
  role: string;
  status: string;
  expires_at: string;
}

export interface OrgJoinInfo {
  organization_id: string;
  organization_name: string;
  role: string;
  type: string;
  expires_at: string;
  inviter_name: string;
  invitee_email?: string;
  is_valid: boolean;
}

export interface OrgJoinRegisterResponse {
  user_id: string;
  name: string;
  email: string;
  knox_token: string;
  matrix_user_id: string;
  matrix_access_token: string;
  matrix_homeserver_url: string;
  matrix_device_id: string;
  default_workspace_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_role: string;
}

export interface AccessApplicationData {
  workEmail: string;
  fullName: string;
  companyName: string;
  role: string;
  problem: string;
  teamSize?: string;
}

export interface AccessApplicationResponse {
  id: number;
  status: string;
  message: string;
}

export interface CommandPolicyItem {
  id: number;
  command: string;
  policy: string;
  user_id?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

/** Known backend error codes → user-facing English messages */
const ERROR_MESSAGES: Record<string, string> = {
  weak_password: 'Password must be at least 12 characters and include letters, numbers, and symbols.',
  email_exists: 'An account with this email already exists.',
  invalid_token: 'This invitation code is invalid or expired.',
  invite_expired: 'This invitation has expired.',
  invite_used: 'This invitation has already been used.',
  invalid_credentials: 'Invalid email or password.',
  initialization_failed: 'Workspace setup is still in progress. Please try again.',
  NEED_CHALLENGE: '', // Handled by frontend — suppress toast
};

class KnoxApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'KnoxApiError';
  }
}

interface RequestOptions extends RequestInit {
  /** When true, suppress the global error toast (use for background polling). */
  silent?: boolean;
  /** reCAPTCHA Enterprise token to inject as X-Recaptcha-Token header. */
  recaptchaToken?: string;
  /** Token type: "checkbox" for v2 fallback, omit for score-based (default). */
  recaptchaType?: 'checkbox';
}

export async function knoxRequest<T>(path: string, init?: RequestOptions): Promise<T> {
  const { silent, recaptchaToken, recaptchaType, ...fetchInit } = init ?? {};
  const defaultHeaders: HeadersInit = fetchInit?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
  // Forward Knox language setting to backend for connector i18n.
  const lang = i18next.language ?? 'en';
  const headers: HeadersInit = {
    ...defaultHeaders,
    'Accept-Language': lang,
    ...(recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : {}),
    ...(recaptchaType ? { 'X-Recaptcha-Type': recaptchaType } : {}),
    ...(fetchInit?.headers ?? {}),
  };
  const res = await fetch(`${KNOX_API_BASE}${path}`, {
    credentials: 'include',
    headers,
    ...fetchInit,
  });
  if (!res.ok) {
    const raw = await res.text();
    let code = '';
    let message = raw || res.statusText;
    try {
      const json = JSON.parse(raw);
      code = json.code ?? '';
      message = ERROR_MESSAGES[code] ?? json.message ?? message;
    } catch {
      // body is not JSON, use raw text
    }
    if (res.status === 401 && !path.startsWith('/api/v1/auth/') && window.location.pathname !== '/login') {
      window.location.replace('/login');
      throw new KnoxApiError(res.status, code, message);
    }
    if (!silent && code !== 'NEED_CHALLENGE') {
      toast.error(message, { id: code || message });
    }
    throw new KnoxApiError(res.status, code, message);
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text);
}

export interface AuditLogItem {
  id: number;
  org_id: string;
  user_id: string;
  user_name: string;
  workspace_id: string;
  conversation_id: string;
  tool: string;
  connection_id: string;
  channel: string;
  host: string;
  command: string;
  args: string;
  file_path: string;
  exit_code: number | null;
  stdout: string;
  error: string;
  duration_ms: number | null;
  batch_id: string | null;
  batch_total: number | null;
  created_at: string;
}

export interface AuditLogListResponse {
  items: AuditLogItem[];
  total: number;
  limit: number;
  offset: number;
}

/** Deduplicate concurrent unparameterized listRooms() calls — multiple
 * useKnoxRooms instances (WorkspaceLayout, KnoxNav, useReplaySender) each
 * fire on mount, sharing a single in-flight promise. */
let listRoomsInFlight: Promise<ListRoomsResponse> | null = null;

/** Deduplicate concurrent countInboxItems() calls — same reasoning as listRooms. */
let countInboxInFlight: Promise<{ count: number }> | null = null;

export const knoxApi = {
  login(params: LoginParams, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(params),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  register(params: RegisterParams, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ ...params, email_verified_token: opts?.emailVerifiedToken }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  setPassword(email: string, password: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ email, password, email_verified_token: opts?.emailVerifiedToken }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  getSession(opts?: { silent?: boolean }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/session', { silent: opts?.silent });
  },

  logout(): Promise<{ ok: boolean }> {
    return knoxRequest('/api/v1/auth/logout', { method: 'POST' });
  },

  oauthAuthorizeUrl(provider: 'google' | 'github', redirectUri?: string): string {
    const params = new URLSearchParams();
    if (redirectUri) params.set('redirect_uri', redirectUri);
    const qs = params.toString();
    return `${KNOX_API_BASE}/api/v1/auth/oauth/${provider}/authorize${qs ? '?' + qs : ''}`;
  },

  getInviteInfo(token: string): Promise<InviteInfoResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  },

  checkEmail(email: string, opts?: { recaptchaToken?: string }): Promise<CheckEmailResponse> {
    return knoxRequest('/api/v1/auth/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
      recaptchaToken: opts?.recaptchaToken,
    });
  },

  forgotPassword(email: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' }): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  sendEmailCode(email: string, purpose: 'register' | 'set_password'): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/send-email-code', {
      method: 'POST',
      body: JSON.stringify({ email, purpose }),
    });
  },

  verifyEmailCode(email: string, code: string, purpose: 'register' | 'set_password'): Promise<{ email_verified_token: string }> {
    return knoxRequest('/api/v1/auth/verify-email-code', {
      method: 'POST',
      body: JSON.stringify({ email, code, purpose }),
    });
  },

  verifyResetToken(token: string): Promise<{ valid: boolean; email?: string; reason?: string }> {
    return knoxRequest('/api/v1/auth/verify-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword, confirm_password: confirmPassword }),
    });
  },

  acceptInvite(token: string, orgName: string, orgSlug?: string): Promise<AcceptInviteResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ org_name: orgName, org_slug: orgSlug }),
    });
  },

  createRoom(params: CreateRoomParams): Promise<Room> {
    return knoxRequest('/api/v1/rooms', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  listRooms(params?: { search?: string }): Promise<ListRoomsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    if (!qs) {
      // Deduplicate concurrent unparameterized calls — multiple components
      // each fire listRooms() on mount, sharing a single in-flight promise.
      if (!listRoomsInFlight) {
        listRoomsInFlight = knoxRequest('/api/v1/rooms').finally(() => { listRoomsInFlight = null; });
      }
      return listRoomsInFlight;
    }
    return knoxRequest(`/api/v1/rooms?${qs}`);
  },

  retryProvisioning(workspaceId: string): Promise<Room> {
    return knoxRequest(`/api/v1/rooms/${encodeURIComponent(workspaceId)}/retry-provisioning`, {
      method: 'POST',
    });
  },

  // --- Elevo proxy APIs (forwarded to elevo-service via knox-service) ---

  getProfile(): Promise<UserProfile> {
    return knoxRequest('/api/v1/elevo/users/me');
  },

  updateProfile(data: UpdateProfileParams): Promise<UserProfile> {
    return knoxRequest('/api/v1/elevo/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  uploadProfileAvatar(file: File): Promise<UploadProfileAvatarResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('context', 'avatar');
    return knoxRequest('/api/v1/elevo/files/upload', {
      method: 'POST',
      body: formData,
    });
  },

  getMyOrgs(): Promise<{ orgs?: OrgInfo[] }> {
    return knoxRequest('/api/v1/elevo/users/me/orgs');
  },

  updateOrg(id: string, data: { name?: string }): Promise<OrgInfo> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getMembers(orgId: string, params?: { keyword?: string; page?: number; page_size?: number }): Promise<MembersResponse> {
    const searchParams = new URLSearchParams();
    if (params?.keyword) searchParams.set('keyword', params.keyword);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    const qs = searchParams.toString();
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members${qs ? '?' + qs : ''}`);
  },

  updateMember(orgId: string, uid: string, data: { role?: string; status?: string }): Promise<void> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteMember(orgId: string, uid: string): Promise<void> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`, {
      method: 'DELETE',
    });
  },

  createInvite(orgId: string, role: string = 'member', opts?: { regenerate?: boolean }): Promise<CreateInviteResponse> {
    const params = opts?.regenerate ? '?regenerate=true' : '';
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/invitations${params}`, {
      method: 'POST',
      body: JSON.stringify({ type: 'link', role }),
    });
  },

  // --- Tenant invitation (Scene 1: admin creates → user registers new org) ---

  getOrgInviteInfo(token: string): Promise<InviteInfoResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  },

  // --- Org member join (ElevoOne org invitations) ---

  getOrgJoinInfo(token: string): Promise<OrgJoinInfo> {
    return knoxRequest(`/api/v1/auth/org-join/${encodeURIComponent(token)}`);
  },


  // --- Logged-in user org creation/joining (no password) ---

  createOrgLoggedIn(params: { token: string; org_name: string; org_slug?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/create-org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  joinOrgLoggedIn(params: { token: string }): Promise<OrgJoinRegisterResponse> {
    return knoxRequest('/api/v1/auth/join-org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Access application (request invite code) ---

  submitAccessApplication(
    data: AccessApplicationData,
    opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' },
  ): Promise<AccessApplicationResponse> {
    return knoxRequest('/api/v1/access-applications', {
      method: 'POST',
      body: JSON.stringify(data),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
      silent: true,
    });
  },

  // --- Org switching ---

  switchOrg(targetOrgId: string): Promise<{ success: boolean }> {
    return knoxRequest('/api/v1/elevo/auth/switch-org', {
      method: 'POST',
      body: JSON.stringify({ target_org_id: targetOrgId }),
    });
  },

  // --- Copilot tool response (ask_human) ---

  submitToolResponse(params: {
    toolCallId: string;
    conversationId: string;
    answers: Record<string, string>;
  }): Promise<{ success: boolean; message?: string }> {
    return knoxRequest('/api/v1/copilot/tool-response', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Copilot tool approval (HIL) ---

  submitApproval(params: {
    conversationId: string;
    approveId: string;
    approved: boolean;
    scope?: 'once' | 'always';
    reason?: string;
  }): Promise<{ success: boolean; approveId: string; approved: boolean }> {
    return knoxRequest('/api/v1/copilot/approve', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Copilot tool detail (lazy load) ---

  getToolDetail(conversationId: string, toolCallId: string): Promise<{
    toolCallId: string;
    toolName: string;
    title?: string;
    state: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
    inputSize?: number;
    outputSize?: number;
  }> {
    return knoxRequest(`/api/v1/tools/${encodeURIComponent(conversationId)}/${encodeURIComponent(toolCallId)}`);
  },

  // --- Inbox ---

  createInboxItem(data: {
    category: string;
    title: string;
    summary?: string;
    priority?: string;
    actions?: string;
    source_type?: string;
    source_ref?: string;
    related_room_id?: string;
  }): Promise<InboxItem> {
    return knoxRequest('/api/v1/inbox', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  listInboxItems(params?: { status?: string; limit?: number; offset?: number }): Promise<InboxItem[]> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return knoxRequest<{ items: InboxItem[] }>(`/api/v1/inbox${qs ? '?' + qs : ''}`, { silent: true }).then(resp => resp.items);
  },

  countInboxItems(status: string = 'open'): Promise<{ count: number }> {
    if (!countInboxInFlight) {
      countInboxInFlight = knoxRequest<{ count: number }>(`/api/v1/inbox/count?status=${encodeURIComponent(status)}`, { silent: true }).finally(() => { countInboxInFlight = null; });
    }
    return countInboxInFlight;
  },

  updateInboxItemStatus(id: string, status: 'resolved' | 'dismissed', action?: string): Promise<{ status: string }> {
    return knoxRequest(`/api/v1/inbox/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, action }),
    });
  },

  updateInboxItemRoom(id: string, relatedRoomId: string): Promise<void> {
    return knoxRequest(`/api/v1/inbox/${encodeURIComponent(id)}/room`, {
      method: 'PATCH',
      body: JSON.stringify({ related_room_id: relatedRoomId }),
    });
  },

  stopAll(roomId: string): Promise<{ stopped: string[] }> {
    return knoxRequest('/api/v1/copilot/stop-all', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
    });
  },

  stopPersona(roomId: string, persona: string): Promise<{ persona: string; stopped: boolean }> {
    return knoxRequest('/api/v1/copilot/stop', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId, persona }),
    });
  },

  clearPersona(roomId: string, persona: string): Promise<void> {
    return knoxRequest('/api/v1/copilot/clear', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId, persona }),
    });
  },

  clearAll(roomId: string): Promise<void> {
    return knoxRequest('/api/v1/copilot/clear-all', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
    });
  },

  activeTyping(roomId: string): Promise<{ bot_user_ids: string[] }> {
    return knoxRequest(`/api/v1/copilot/active-typing?room_id=${encodeURIComponent(roomId)}`);
  },

  // --- Command Policies ---

  listCommandPolicies(scope?: 'org' | 'personal'): Promise<{ policies: CommandPolicyItem[] }> {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const qs = params.toString();
    return knoxRequest<{ policies: CommandPolicyItem[] }>(`/api/v1/command-policies${qs ? '?' + qs : ''}`);
  },

  createCommandPolicy(data: { command: string; policy?: 'allow' | 'block'; scope: 'org' | 'personal' }): Promise<{ id: number; command: string; policy: string }> {
    return knoxRequest('/api/v1/command-policies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteCommandPolicy(id: number): Promise<void> {
    return knoxRequest(`/api/v1/command-policies/${id}`, { method: 'DELETE' });
  },

  syncMemberAvatar(roomId: string): Promise<{ status: string; avatar_url?: string }> {
    return knoxRequest('/api/v1/rooms/sync-room-profiles', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
      silent: true,
    });
  },

  listAuditLogs(params?: {
    tool?: string;
    user_id?: string;
    host?: string;
    batch_id?: string;
    status?: string;
    search?: string;
    start_time?: string;
    end_time?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogListResponse> {
    const sp = new URLSearchParams();
    if (params?.tool) sp.set('tool', params.tool);
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.host) sp.set('host', params.host);
    if (params?.batch_id) sp.set('batch_id', params.batch_id);
    if (params?.status) sp.set('status', params.status);
    if (params?.search) sp.set('search', params.search);
    if (params?.start_time) sp.set('start_time', params.start_time);
    if (params?.end_time) sp.set('end_time', params.end_time);
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.offset) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return knoxRequest(`/api/v1/audit-logs${qs ? '?' + qs : ''}`);
  },
};

export { KnoxApiError };

// --- Credits types ---

export interface CreditBalanceResp {
  balance: number;
  unit: string;
}

export interface RechargeItem {
  id: number;
  amount: number;
  note: string;
  created_at: string;
}

export interface ListRechargesResp {
  total: number;
  items: RechargeItem[];
}

export interface UsageItem {
  period: string;
  quota: number;
  requests: number;
}

export interface ListUsageResp {
  total: number;
  items: UsageItem[];
}

// --- Credits API ---

export const creditsApi = {
  async getBalance(): Promise<CreditBalanceResp> {
    return await knoxRequest<CreditBalanceResp>('/api/v1/credits/balance');
  },

  async listRecharges(page = 1, pageSize = 20): Promise<ListRechargesResp> {
    return await knoxRequest<ListRechargesResp>(`/api/v1/credits/recharges?page=${page}&page_size=${pageSize}`);
  },

  async listUsage(params?: { start?: string; end?: string }): Promise<ListUsageResp> {
    const sp = new URLSearchParams();
    if (params?.start) sp.set('start', params.start);
    if (params?.end) sp.set('end', params.end);
    const qs = sp.toString();
    return await knoxRequest<ListUsageResp>(`/api/v1/credits/usage${qs ? '?' + qs : ''}`);
  },

  async listUsageStats(params?: { start?: string; end?: string }): Promise<{ total_quota: number; total_requests: number }> {
    const sp = new URLSearchParams();
    if (params?.start) sp.set('start', params.start);
    if (params?.end) sp.set('end', params.end);
    const qs = sp.toString();
    return await knoxRequest<{ total_quota: number; total_requests: number }>(`/api/v1/credits/usage/stats${qs ? '?' + qs : ''}`);
  },

  async submitRechargeRequest(reason: string): Promise<{ id: number; status: string }> {
    return await knoxRequest('/api/v1/credits/recharge-requests', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};
